<template>
    <div
        id="pdf-box"
        v-touch="{
            left: () => nextDocument(),
            right: () => prevDocument()
        }"
    >
       <DefaultLoading v-if="loading || loadingBar" :loading="loading || loadingBar" />

       <v-container v-if="docError">
          <v-row class="fill-height" align-content="center" justify="center">
             <v-col cols="12">
                <v-alert border="top" colored-border type="error" elevation="2">
                   {{ $t("document_error") }}
                </v-alert>
             </v-col>
          </v-row>
       </v-container>

       <v-container v-if="error && !this.otpDialog">
          <v-row class="fill-height mt-5" align-content="center" justify="center">
             <v-col cols="12">
                <v-alert text color="#FF000D" border="left">
                   <v-icon color="#FF000D">mdi-close-circle-outline</v-icon>
                   <strong>{{ errorMsg }}</strong>
                </v-alert>
             </v-col>
          </v-row>
       </v-container>

       <v-container v-if="status === 'SIGNED'">
          <v-alert class="mt-5" border="left" text type="warning">
             {{ $t("document_already_signed") }}
          </v-alert>
       </v-container>

       <v-container v-if="!docError && status !== 'SIGNED' && !loading">
          <div id="pdf-view" class="col-md-12 col-sm-12 pa-0" style="height:calc(80vh - 20px);">
               <PDFJSViewer
                 v-if="pdfUrl"
                 :pdf-url="pdfUrl"
                 public-path="/pdfjs"
                 @documentError="documentError"
               />
            </div>
       </v-container>

       <div class="footer-message" v-if="!docError && status !== 'SIGNED' && !loading">
          <div>
             <v-row no-gutters>
                <v-container class="py-0 fill-height">

                   <v-row align="center" class="w-100">
                      <div class="col-md-8 col-xs-12 text-left terms">
                         <v-list>
                            <v-list-item>
                               <template v-slot:default="{ active }">
                                  <v-list-item-action>
                                     <v-checkbox @change="changeConfirm()" class="custom-checkbox"
                                        v-model="terms_accepted" :input-value="active"></v-checkbox>
                                  </v-list-item-action>
                                  <v-list-item-content class="terms-conditions-text">
                                     {{ $t("read_understand_doc") }}
                                  </v-list-item-content>
                               </template>
                            </v-list-item>
                         </v-list>

                         <v-list class="mt-1 observations-list" v-if="signatureComments">
                            <v-list-item
                               class="observations-item"
                               dense
                               @click="openCommentDialog"
                               role="button"
                               tabindex="0"
                               @keyup.enter="openCommentDialog"
                               @keyup.space.prevent="openCommentDialog"
                            >
                               <v-list-item-action class="obs-action">
                                 <v-icon size="24" color="#FFFFFF">mdi-comment-text-outline</v-icon>
                               </v-list-item-action>
                               <v-list-item-content>
                                 <v-list-item-title class="obs-title">
                                   {{ $t("sign_comments") }}
                                   <em v-if="comments" class="obs-comment"> {{ comments }}</em>
                                 </v-list-item-title>
                               </v-list-item-content>
                            </v-list-item>
                         </v-list>
                      </div>

                      <div class="col-md-2 col-6 sign">
                         <v-btn
                           large
                           block
                           :loading="loading || isLocating"
                           @click="openLocationDialog"
                           :style="{
                               backgroundColor: signatureButtonColor,
                               color: signatureTextButtonColor,
                               opacity: confirm ? 1 : 0.3,
                               cursor:  confirm ? 'pointer' : 'default',
                               pointerEvents: confirm ? 'auto' : 'none'
                             }"
                         >
                           <v-icon class="mr-2">mdi-pencil-box-outline</v-icon>
                           {{ $t("sign") }}
                         </v-btn>
                      </div>

                      <div class="col-md-2 col-6 reject" v-if="!isNoReject">
                         <v-btn
                           large
                           block
                           @click="reject"
                           :style="{
                             backgroundColor: rejectButtonColor,
                             color: rejectTextButtonColor
                           }"
                         >
                           <v-icon class="mr-2">mdi-close-circle-outline</v-icon>
                           {{ $t("reject") }}
                         </v-btn>
                      </div>
                   </v-row>

                   <v-row v-if="showBackToList || user" align="center" class="mt-2 w-100 mb-1">

                      <v-col cols="6" class="text-left py-0">
                          <v-btn
                            v-if="showBackToList"
                            text
                            small
                            @click="isLogging"
                            class="back-btn"
                            :style="{ color: 'var(--footer-txt-color, #FFFFFF)' }"
                          >
                            <v-icon left small>mdi-arrow-left</v-icon>
                            {{ $t("back_to_documents_list") }}
                          </v-btn>
                      </v-col>

                      <v-col cols="6" class="d-flex justify-end align-center py-0" v-if="user && totalDocuments > 1">
                         <v-btn
                            icon
                            fab
                            x-small
                            @click="prevDocument"
                            color="white"
                            class="nav-touch-btn mr-1"
                         >
                            <v-icon>mdi-chevron-left</v-icon>
                         </v-btn>

                         <div class="mx-2 text-center" style="min-width: 45px">
                            <span class="white--text caption font-weight-bold d-block" style="line-height: 1">
                               {{ currentIndex + 1 }} / {{ totalDocuments }}
                            </span>
                         </div>

                         <v-btn
                            icon
                            fab
                            x-small
                            @click="nextDocument"
                            color="white"
                            class="nav-touch-btn ml-1"
                         >
                            <v-icon>mdi-chevron-right</v-icon>
                         </v-btn>
                      </v-col>
                   </v-row>

                </v-container>
             </v-row>
          </div>
       </div>

       <sign-confirm-otp-modal
           v-if="otpDialog"
           :dialog="otpDialog"
           :key="requestId"
           :errorMsg="errorMsg"
           :error="error"
           :phone-number="userPhone || '...'"
           @update:dialog="otpDialog = $event"
           @submitOtpCode="submitOtpCode"
           @resendOtpCode="sendOtpCode"
       />

       <certificate-pin-modal
         :dialog="certificatePinCodeDialog"
         :error="error"
         :error-msg="errorMsg"
         @submitCertificatePin="submitCertificatePin"
         @update:dialog="certificatePinCodeDialog = $event" />

       <sign-confirm-pad-modal
         :dialog="padDialog"
         @submitSignPad="submitSignPad"
         @update:dialog="padDialog = $event" />

       <sign-comments-modal
         :dialog="commentsDialog"
         :error="error"
         :error-msg="errorMsg"
         @submitComments="submitComments"
         @update:dialog="commentsDialog = $event" />
    </div>
</template>

<style scoped>
/* Mejora visual para los botones de navegación en móvil */
.nav-touch-btn {
    background-color: rgba(255, 255, 255, 0.15) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
}
.nav-touch-btn:active {
    background-color: rgba(255, 255, 255, 0.3) !important;
}
</style>

<script>
// import PdfViewer from "../components/shared/PdfViewer.component";
import PDFJSViewer from "../components/shared/PDFJSViewer.component.vue";
import SignConfirmOtpModal from "../components/shared/SignConfirmOtpModal.component";
import SignConfirmPadModal from "../components/shared/SignConfirmPadModal.component";
import DefaultLoading from "../components/shared/DefaultLoading.component";
import { SignaturesService } from "../services/signatures-service";
import moment from "moment";
import CertificatePinModal from "../components/shared/CertificatePinModal.component.vue";
import SignCommentsModal from "../components/shared/SignCommentsModal.component";

export default {
	components: {
		// PdfViewer,
		PDFJSViewer,
		SignConfirmOtpModal,
		SignConfirmPadModal,
		DefaultLoading,
		CertificatePinModal,
		SignCommentsModal,
	},

	data: function () {
		return {
            isLocating: false, // <-- Nueva variable para el feedback
            pdfUrl: null,
			terms_accepted: null,
			confirm: false,
			loading: true,
			loadingBar: false,
			docError: false,
			error: false,
			requestId: localStorage.getItem("request_id"),
		//	token: localStorage.getItem("access_token"),
			token: localStorage.getItem("auth_user_token") || localStorage.getItem("access_token"),
			authCitizenId: localStorage.getItem("auth_citizen_id"),
			isAuthCitizen: localStorage.getItem("is_auth_citizen"),
			showPdf: false,
			otpStatus: false,
			otpDialog: false,
			otpSend: false,
			otpCode: "",
			padDialog: false,
			padStatus: false,
			padImage: "",
			userLatitude: "",
			userLongitude: "",
			errorMsg: "",
			submitLocation: false,
			certificatePinCodeDialog: false,
			requestType: "",
			userPhone: "",
			rejectComments: false,
            commentsDialog: false,
            comments: "",
            ip: "",
            userOS: "",
            requestComments: "",
            userAgent: "",
            user: localStorage.getItem("user"),
            totalDocuments: 0,
            currentIndex:0,
            docList: [],
		};
	},
    beforeRouteEnter(to, from, next) {
        // NO tienes acceso a `this` aquí, así que no llames a this.loadRequestData() directamente.
        next(vm => {
          // cuando ya existe la instancia `vm`, puedes invocar su método:
          vm.loadRequestData()
        });
      },

	watch: {
		otpSend() {
			if (this.otpStatus) {
				this.otpDialog = true;
				this.padDialog = false;
			}
		},
		submitLocation() {
			this.signDocument();
		},

		'$store.state.common.signatureColor'(newColor) {
            document.documentElement.style.setProperty('--signature-bg-color', newColor);
          },
          '$store.state.common.signatureTextColor'(newColor) {
            document.documentElement.style.setProperty('--signature-text-color', newColor);
          },
	},

	computed: {
        // ... tus otras computadas ...
        appVersion() {
            return this.$store.state.common.appVersion;
        },
        showBackToList() {
            let back;
            let user = localStorage.getItem("user");
            let list = localStorage.getItem("list");
            if (user || list) {
                back = true;
                }
            else {
                back = false;
                }
            return back;
        },
		auth() {
			return this.$store.state.common.auth;
		},
		status() {
			return this.$store.state.common.status;
		},
		authId() {
			return this.$store.state.common.authId;
		},
		hasAssociation() {
			return this.$store.getters['common/hasAssociation']
		},
		isNoReject() {
            return localStorage.getItem('noreject') === 'true'
        },

        signatureButtonColor() {
            return this.$store.state.common.signatureColor;
        },
        signatureTextButtonColor() {
            return this.$store.state.common.signatureTextColor;
        },
        rejectButtonColor() {
            return this.$store.state.common.rejectColor;
        },
        rejectTextButtonColor() {
            return this.$store.state.common.rejectTextColor;
        }

	},
async mounted() {
        console.log('[PdfView] mounted');
        this.loadRequestData();
        this.otpSend = false;
        this.loadDocument();
        this.totalDocuments = localStorage.getItem('pendingDocs');
        // this.currentIndex = this.currentListIndex();
        this.docList=this.readDocList();

        document.documentElement.style.setProperty('--signature-bg-color', this.$store.state.common.signatureColor);
        document.documentElement.style.setProperty('--signature-text-color', this.$store.state.common.signatureTextColor);

        //Lectura del UserAgent
        this.userAgent = navigator.userAgent;

        // ip será el string o null si falla
        try {
          this.ip = await this.fetchPublicIP();
          console.log('IP:', this.ip);
        } catch (e) {
          this.ip = null;
          console.error('Error obteniendo IP:', e);
        }

        const { evidence } = await this.collectOSEvidence();
        const payload = this.cleanEvidence(evidence);
        fetch('/api/os-evidence', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ evidence: payload })
        });
          console.log('EVIDENCE:', JSON.parse(JSON.stringify(payload)));

          // enviar evidence/timestamp a tu backend para mapping posterior
    },

	methods: {

        readDocList() {
            console.log("Ejecutando ReadDocList");
            const savedDocs = localStorage.getItem('docList');

            if (savedDocs) {
                try {
                    // 1. Transformamos el texto en un objeto real
                    const parsedList = JSON.parse(savedDocs);

                    // 2. Verificamos que sea un array antes de asignarlo
                    if (Array.isArray(parsedList)) {
                        this.docList = parsedList;
                        this.currentIndex = this.currentListIndex();

                        // USA COMA (,) para ver los datos reales en la consola
                        console.log("✅ Lista cargada correctamente. Total documentos:", this.docList.length);
                        console.log("ID en la Lista:", this.docList[this.currentIndex].id);
                    } else {
                        console.error("La 'docList' en localStorage no es un array válido.");
                    }
                } catch (e) {
                    console.error("Error al parsear docList desde localStorage:", e);
                    this.docList = [];
                }
            } else {
                console.warn("No se encontró 'docList' en el localStorage.");
                this.docList = [];
            }
        },

        currentListIndex() {
                // Usamos == en lugar de === para evitar errores si uno es String y el otro Number
                if (!this.docList) return -1;

                return this.docList.findIndex(doc => doc.id == this.requestId);
            },

        nextDocument() {
            console.log("Request Id: " + this.requestId + " Navegando al siguiente Doc");
            this.setNextRequestParams();
            // this.loadNewDocument();
        },

        setNextRequestParams() {
            // 1. RE-INTENTO DE LECTURA (Si la variable está vacía, la forzamos)
            if (!this.docList || this.docList.length === 0) {
                console.log("🔄 Re-intentando lectura de docList desde Storage...");
                this.readDocList();
            }

            // 2. Verificación de seguridad tras el re-intento
            if (!this.docList || this.docList.length === 0) {
                console.error("❌ Lista no cargada: El Storage está vacío o corrupto");
                return;
            }

            // 3. Recalcular el índice actual justo antes de movernos
            // (Esto es más seguro que confiar en this.currentIndex guardado)
            const indexActual = this.docList.findIndex(doc => String(doc.id) === String(this.requestId));

            // 4. Calcular siguiente (Circular)
            const nextIndex = (indexActual + 1) % this.docList.length;
            const proximoDoc = this.docList[nextIndex];

            if (proximoDoc) {
                console.log("➡️ Saltando al ID:", proximoDoc.id);

                this.requestId = proximoDoc.id;
                // this.token = proximoDoc.apiToken;
                // this.authid = proximoDoc.signerId.id;

                // Actualizamos el índice guardado para el footer
                this.currentIndex = nextIndex;

                this.loadRequestData();
                // Importante cargar el PDF físico también
                if (typeof this.loadDocument === 'function') this.loadDocument();

                this.$router.push({ params: { id: proximoDoc.id } }).catch(() => {});
            }
        },

        prevDocument() {
            console.log("Request Id: " + this.requestId + " Navegando al Doc anterior");
            this.setPrevRequestParams();
        },

        setPrevRequestParams() {
            // 1. RE-INTENTO DE LECTURA (Por si la variable se vació en memoria)
            if (!this.docList || this.docList.length === 0) {
                console.log("🔄 Re-intentando lectura de docList para Prev...");
                this.readDocList();
            }

            // 2. Verificación de seguridad
            if (!this.docList || this.docList.length === 0) {
                console.error("❌ Lista no cargada: El Storage está vacío");
                return;
            }

            // 3. Encontrar el índice actual (Comparación segura de Strings)
            const indexActual = this.docList.findIndex(doc => String(doc.id) === String(this.requestId));

            // 4. Calcular índice anterior (Fórmula circular)
            // Sumamos la longitud de la lista antes del módulo para que el resultado nunca sea negativo
            const prevIndex = (indexActual - 1 + this.docList.length) % this.docList.length;

            // 5. Obtener el objeto del documento anterior
            const anteriorDoc = this.docList[prevIndex];

            if (anteriorDoc) {
                console.log("⬅️ Saltando al ID anterior:", anteriorDoc.id);

                // Actualizamos estado local
                this.requestId = anteriorDoc.id;
                // this.token = anteriorDoc.apiToken; // Usamos su token específico
                this.currentIndex = prevIndex;     // Actualizamos el índice para el footer

                // Forzamos la recarga de datos y PDF
                this.loadRequestData();
                if (typeof this.loadDocument === 'function') {
                    this.loadDocument();
                }

                // Actualizamos la URL en el navegador
                this.$router.push({ params: { id: anteriorDoc.id } }).catch(() => {});

                // Reset de términos aceptados
                this.terms_accepted = false;
                this.confirm = false;
            }
        },


        cleanEvidence(input) {
              const seen = new WeakMap();

              // _clean como arrow para evitar problemas con declaraciones dentro de objetos
              const _clean = (value) => {
                // primitivos
                if (value === null) return null;
                const t = typeof value;
                if (t === 'string' || t === 'number' || t === 'boolean') return value;
                if (t === 'undefined') return null;
                if (t === 'function' || t === 'symbol') return undefined; // omitimos

                // Date
                if (value instanceof Date) return value.toISOString();

                // RegExp
                if (value instanceof RegExp) return value.toString();

                // Si ya lo vimos, evitar bucles (marcar como Circular)
                if (typeof value === 'object') {
                  if (seen.has(value)) return '[Circular]';
                }

                // Arrays
                if (Array.isArray(value)) {
                  const arrCopy = [];
                  seen.set(value, arrCopy);
                  for (let i = 0; i < value.length; i++) {
                    const v = _clean(value[i]);
                    arrCopy.push(v === undefined ? null : v);
                  }
                  return arrCopy;
                }

                // Objetos genéricos: copiar solo propiedades propias enumerables
                if (typeof value === 'object') {
                  const out = Object.create(null); // sin prototipo
                  seen.set(value, out);

                  for (const key of Object.keys(value)) {
                    if (key === '__ob__') continue; // quitar metadata de Vue2
                    // opcional: if (key[0] === '_') continue; // quitar privadas
                    try {
                      const v = value[key];
                      const cleaned = _clean(v);
                      out[key] = cleaned === undefined ? null : cleaned;
                    } catch (e) {
                      out[key] = `Error reading property: ${String(e)}`;
                    }
                  }

                  return out;
                }

                // fallback
                return null;
              };

              return _clean(input);
            },

        async collectOSEvidence() {
            const evidence = {};

            // Básicos del UA / plataforma
            evidence.userAgent = navigator.userAgent || null;
            evidence.platform = navigator.platform || null;
            evidence.appVersion = navigator.appVersion || null;
            evidence.vendor = navigator.vendor || null;
            evidence.oscpu = navigator.oscpu || null; // disponible en algunos navegadores

            // navigator.userAgentData (moderno): marcas y high-entropy si está disponible
            if (navigator.userAgentData) {
              evidence.userAgentData = {
                mobile: navigator.userAgentData.mobile || false,
                brands: navigator.userAgentData.brands || navigator.userAgentData.uaList || null
              };
              if (typeof navigator.userAgentData.getHighEntropyValues === 'function') {
                try {
                  const high = await navigator.userAgentData.getHighEntropyValues([
                    'platform',
                    'platformVersion',
                    'architecture',
                    'model',
                    'uaFullVersion',
                    'fullVersionList'
                  ]);
                  evidence.userAgentData.highEntropy = high;
                } catch (err) {
                  evidence.userAgentData.highEntropy = null;
                }
              } else {
                evidence.userAgentData.highEntropy = null;
              }
            } else {
              evidence.userAgentData = null;
            }

            // Hardware / capacidades
            evidence.hardwareConcurrency = navigator.hardwareConcurrency ?? null; // núcleos lógicos
            evidence.deviceMemory = navigator.deviceMemory ?? null; // aproximado en GB
            evidence.maxTouchPoints = navigator.maxTouchPoints ?? 0;
            evidence.language = navigator.language || null;
            evidence.languages = navigator.languages || null;
            evidence.cookieEnabled = navigator.cookieEnabled ?? null;
            evidence.doNotTrack = navigator.doNotTrack ?? null;

            // Pantalla / DPR / timezone
            evidence.screen = {
              width: (screen && screen.width) || null,
              height: (screen && screen.height) || null,
              availWidth: (screen && screen.availWidth) || null,
              availHeight: (screen && screen.availHeight) || null,
              pixelRatio: window.devicePixelRatio || null
            };
            try { evidence.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (e) { evidence.timezone = null; }

            // GPU renderer (si está disponible)
            try {
              const canvas = document.createElement('canvas');
              const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
              if (gl) {
                const dbg = gl.getExtension('WEBGL_debug_renderer_info');
                evidence.gpu = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : null;
              } else {
                evidence.gpu = null;
              }
            } catch (e) {
              evidence.gpu = null;
            }

            // Otros detalles útiles
            evidence.appName = navigator.appName || null;
            evidence.onLine = navigator.onLine ?? null;

            // const timestamp = new Date().toISOString();
            return { evidence};
          },

        async fetchPublicIP({ url = 'https://api.ipify.org?format=json', timeout = 5000 } = {}) {
             const controller = new AbortController();
             const timer = setTimeout(() => controller.abort(), timeout);

             try {
               const resp = await fetch(url, { signal: controller.signal, mode: 'cors' });
               if (!resp.ok) return null;

               const contentType = resp.headers.get('content-type') || '';
               let ip = null;

               if (contentType.includes('application/json')) {
                 const data = await resp.json();
                 ip = data && data.ip ? String(data.ip) : null;
               } else {
                 const text = await resp.text();
                 ip = text ? text.trim() : null;
               }

               // Guarda en el estado del componente para usarlo en la plantilla
               this.publicIP = ip;
               return ip;
             } catch (err) {
               // fetch falló o se abortó (timeout)
               return null;
             } finally {
               clearTimeout(timer);
             }
           },

        openCommentDialog() {
            // Aquí puedes abrir un modal o mostrar un campo de texto para comentarios
            console.log("Abrir diálogo de comentarios");
            this.commentsDialog = true;
          },

        async waitForDialogClose(dialogName) {
            return new Promise((resolve) => {
                // 🔍 Espera a que Vue actualice el DOM antes de observar cambios
                this.$nextTick(() => {
                    const watcher = this.$watch(dialogName, (newValue) => {
                        console.log(`Esperando cierre de ${dialogName}:`, newValue);
                        if (!newValue) {  // 🔴 Cuando el diálogo se cierra
                            console.log(`${dialogName} ha sido cerrado`);
                            watcher();  // 🛑 Detener el watcher
                            resolve();  // 🔄 Continuar con el siguiente paso
                        }
                    }, { immediate: true }); // 👈🏽 Comienza la observación de inmediato
                });
            });
        },

        isLogging() {
            this.authid="";
            let association=localStorage.getItem("currentAssociation");
            let isCustomized=localStorage.getItem("isCustomizedUser");
            console.log(association);
            console.log(localStorage.getItem("auth_user_token"));

            if (localStorage.getItem("auth_user_token") != null) {
                this.$router.push({
                    name: isCustomized ? "custom-my-signatures" : "my-signatures",
                    params: isCustomized ? { association } : {}
                });

            } else {
                // Si no hay authCitizenId, redirigir directamente a DocumentList
                const routeName = this.hasAssociation ? "custom-multiple-pdf" : "multiple-pdf";
                this.$router.push({ name: routeName });
                console.log(this.authCitizenId);
            }
        },

		changeConfirm() {
			this.confirm = !this.confirm;
		},
		loadRequestData() {
			this.loading = true;
			this.$store
				.dispatch("common/getRequestData", {
					token: this.token,
					requestId: this.requestId,
					authId: this.authId,
				})
				.then((response) => {
					this.loading = false;
					const signRequest = response.data;
					this.$store.commit('forms/setHasPendingSignerForm', this.hasPendingSignerForm);
                    this.$store.commit('attachments/setHasAttachments', this.hasAttachments);

					this.padStatus = signRequest.pad;
					this.otpStatus = signRequest.otp;
					this.userPhone = signRequest.signerPhone;
					this.rejectComments = signRequest.hasComments;
					this.signatureComments = signRequest.commentsSigning;
					this.rejectComments = signRequest.hasComments;
					console.log("Móvil: " + this.userPhone);

					if (signRequest.id == this.$route.params.id) {
						this.document = signRequest.signatureTransaction.description;
						this.signerName = signRequest.signerName;
						this.ownerName = signRequest.signatureTransaction.ownerName;
						this.title = signRequest.signatureTransaction.document.title;
						// this.comments = signRequest.signatureTransaction.comments;
						this.date = signRequest.signatureTransaction.dueDate;

					}
				})
				.catch(() => {
					this.loading = false;
				});
		},
		async signDocument() {
            console.log("Dentro de signDocument");
            if (this.isSigning) {
                console.warn("⚠️ Ya se está ejecutando signDocument(), evitando duplicación...");
                return;
            }
            this.isSigning = true;
            this.requestType=localStorage.getItem("type");
            console.log("requestType en signDocument:", this.requestType, typeof this.requestType);

			this.error = false;
			console.log(this.requestType);
			if (this.padStatus) {
                console.log("activando PAD");
				this.padDialog = true;
				await this.waitForDialogClose("padDialog");
			}
			if (this.requestType === "HUMAN") {
                console.log("Mostrando Diálogo de PIN");
                this.certificatePinCodeDialog = true;
                await this.waitForDialogClose("certificatePinCodeDialog");
            }
            if (this.otpStatus) {

                // 1. Forzamos a que Vue termine de procesar cualquier cambio pendiente en el DOM
                await this.$nextTick();

                console.log("📌 Enviando OTP...");
                await this.sendOtpCode();  // ✅ Solo se ejecuta UNA VEZ
                return; // ⛔ Evita ejecutar `submitDocument()` hasta recibir OTP
            }

			console.log("Ejecutando submitDocument");
			this.submitDocument();
			this.isSigning = false;

		},

		getSubmitData() {
			let submitData = {
				comments: this.comments,
				location:
					this.userLatitude && this.userLongitude
						? this.userLatitude + ", " + this.userLongitude
						: "",
				lastRefresh: moment(String(new Date())).format("YYYY-MM-DDTHH:mm:ssZZ"),
				ipAddress: this.ip,
				userAgent: this.userAgent,
			};


			console.log(this.requestType);
			if (this.requestType == "ONETIMEWITHID") {
				submitData["authId"] = this.authId;
			}

			if (this.otpStatus) {
				submitData["otp"] = this.otpCode;
			}

			if (this.padStatus) {
				submitData["signatureImage"] = this.padImage;
			}

			if (this.requestType == "HUMAN"){
                submitData["password"] = this.certificatePinCode;
			}

			return submitData;
		},

        submitDocument() {
            console.log ("Dentro de SubmitDocument");
            this.error = false;

            if (!this.requestId) {
                this.error = true;
                return;
            }

            this.loadingBar = true;
            console.log("Ejecutando signDocument");
            console.log("Comentarios de firmante: ", this.comments);
            console.log("IP: ", this.ip);
            SignaturesService.signDocument(this.requestId, this.getSubmitData(), this.ip)
                .then((response) => {
                    this.loadingBar = false;
                    this.status = response.data.status;

                    if (response.data.status === "SIGNED") {
                        this.certificatePinCodeDialog = false; // ✅ cerrar el modal solo si firma correctamente
                        this.$router.push({ name: `${this.hasAssociation ? 'custom-' : ''}summary` });
                    } else {
                        this.showPdf = true;
                    }
                })
                .catch((error) => {
                    this.loadingBar = false;
                    this.showPdf = false;

                    const description = error?.response?.data?.description || "";

                    if (error.response.status === 422) {
                        if (description === "Invalid otp code") {
                            this.error = true;
                            this.errorMsg = this.$t("invalid_otp");
                            this.otpDialog = true;
                        } else if (description === "SYSTEM.PKCS11_PIN_INCORRECT") {
                            this.error = true;
                            this.errorMsg = this.$t("invalid_pin");
                            this.certificatePinCodeDialog = true; // ❗️ mantener abierto el diálogo de PIN
                        } else {
                            this.error = true;
                            this.errorMsg = description;
                        }
                    }

                    if (error.response.status === 403) {
                        this.error = true;
                        this.errorMsg = description || error.response.data.description;
                    }

                    if (error.response.status === 500) {
                        this.error = true;
                        this.errorMsg = this.$t("server_error");
                    }

                    document.body.scrollTop = 0;
                    document.documentElement.scrollTop = 0;
                });
        },


        async sendOtpCode() {
            if (this.isSendingOtp) {
                console.warn("⚠️ OTP ya solicitado, evitando duplicación...");
                return;
            }

            this.isSendingOtp = true;
            this.error = false;

            console.log("📌 Enviando OTP a la API...");
            this.otpDialog = true;

            let data = {
                comments: "",
                authId: this.authId,
                location: "",
                lastRefresh: "",
                otp: "",
                signatureImage: "",
            };

            try {
                    // Ejecutamos la llamada y ESPERAMOS a que termine
                    await SignaturesService.sendOtp(this.requestId, data, this.token);

                    // Si por un milagro entrara aquí (200 OK), también abrimos
                    this.openOtpInterface();
                } catch (error) {
                    // Aquí es donde entra tu "error natural" 422
                    console.log("📌 Paso 2: SMS forzado con éxito (Error 422 recibido)");

                    const description = error?.response?.data?.description || "";

                    // Solo abrimos el diálogo si el error es el esperado
                    if (error.response?.status === 422 || description.includes("otp")) {
                        this.openOtpInterface();
                    } else {
                        // Si es un error real (500, 403), mostramos el error y no abrimos nada
                        this.error = true;
                        this.errorMsg = description || "Error de comunicación";
                    }
                } finally {
                    this.isSendingOtp = false;
                    this.loadingBar = false;
                }
        },

        // ESTO ES LO QUE TE FALTA AÑADIR:
        openOtpInterface() {
            this.$nextTick(() => {
                this.otpDialog = true;
                console.log("📌 Paso 3: Diálogo OTP abierto tras completar proceso de red");
            });
        },

         async reject() {
           // normaliza y recorta
           const commentText = (this.comments || "").trim();

           // Si requiere comentario y no hay texto: ir a 'rejected'
           if (this.rejectComments && commentText === "") {
             const routeName = `${this.hasAssociation ? "custom-" : ""}rejected`;
             await this.$router.push({ name: routeName });
             return;
           }

           // Si hay texto: enviar el rechazo y luego navegar a summary
           const payload = { comments: commentText, authId: this.authId };

           this.loading = true;
           try {
             console.log("Enviando Reject con Data:", payload);
             const response = await SignaturesService.rejectDocument(this.requestId, payload);
             console.log("RESPUESTA REJECT:", response);

             const status = response && response.data ? response.data.status : undefined;
             if (status === "REJECTED") {
               this.rejectSuccess = true;
               this.signatureTransactionData = (response.data && response.data.signatureTransaction) || null;

               // Si tienes una función para refrescar la lista, llámala si existe
               if (typeof this.createRequestList === "function") {
                 try { this.createRequestList(response.data); }
                 catch (e) { console.warn("createRequestList fallo:", e); }
               }

               // limpia comentarios si quieres
               this.comments = "";
             } else {
               // respuesta inesperada: marcar error y no navegar
               this.error = true;
               this.errorMsg = this.$t("unexpected_server_response") || "Unexpected server response";
               return;
             }

             // Navega a summary solo después del OK
             const routeName = `${this.hasAssociation ? "custom-" : ""}summary`;
             await this.$router.push({ name: routeName });

           } catch (err) {
             console.error("Error al enviar Reject:", err);
             this.rejectSuccess = false;
             if (err && err.response && err.response.data) {
               this.error = true;
               this.errorMsg = err.response.data.description || err.response.data.message || String(err);
             } else {
               this.error = true;
               this.errorMsg = err.message || String(err);
             }
           } finally {
             this.loading = false;
           }
         },


		documentError(value) {
			this.docError = value;
		},
submitOtpCode(otpCode) {
  console.log("Recibido OTP:", otpCode);
  this.otpCode = otpCode;

//  this.otpDialog = false; // 🔥 Cerrar el diálogo OTP

  this.$nextTick(() => {
    console.log("OTP cerrado, enviando documento...");
    this.submitDocument(); // 💡 Ahora el flujo sigue correctamente
  });
},

submitSignPad(padImage) {
  console.log("Dentro de submitSignPad, imagen recibida:", padImage);
  this.padImage = padImage;

  this.padDialog = false; // 🔥 Cierra el modal PAD

  this.$nextTick(() => {
    // 🔥 Espera a que el modal se cierre antes de continuar
    console.log("PAD cerrado, continuando con PIN o OTP...");

    // 🔥 Si se requiere PIN, se abre el diálogo de PIN
    if (this.requestType === "HUMAN") {
      console.log("Abriendo diálogo de PIN...");
      this.certificatePinCodeDialog = true;
      return; // ⛔ Esperamos a que cierre el PIN antes de continuar
    }

    // 🔥 Si se requiere OTP, se abre el diálogo de OTP
    if (this.otpStatus) {
      console.log("Abriendo diálogo de OTP...");
      this.otpDialog = true;
      return;
    }

    // 🔥 Si no hay más validaciones, enviamos el documento
    console.log("Ejecutando submitDocument()");
    this.submitDocument();
  });
},

submitCertificatePin(pinCode) {
	console.log("Recibido PIN:", pinCode);
	this.certificatePinCode = pinCode;

	this.$nextTick(() => {
		console.log("PIN cerrado, verificando OTP...");

		if (this.otpStatus) {
			console.log("Abriendo diálogo de OTP...");
			this.otpDialog = true;
			return;
		}

		console.log("Ejecutando submitDocument()");
		this.submitDocument();
	});
},

submitComments(comments) {
	console.log("Recibidos comentarios:", comments);
	this.comments = comments;
},

     async loadDocument() {
       this.loading = true;
       this.docError = false;
       try {
         const resp = await SignaturesService.getSignatureRequestDocument(
           this.requestId,
           this.authId,
           { responseType: "arraybuffer" }
         );
         const blob = new Blob([resp.data], { type: "application/pdf" });
         this.pdfUrl = URL.createObjectURL(blob);
         this.$emit("documentError", false);
       } catch (err) {
         if (err.response?.status === 404) {
           this.docError = true;
           this.$emit("documentError", true);
         }
       } finally {
         this.loading = false;
       }
     },


        openLocationDialog() {
            console.log("Request Type en Location: " + this.requestType);
            this.requestType=localStorage.getItem("type");

            this.isLocating = true;
            this.submitLocation = false; // Reset para asegurar que el watch detecte el cambio

            if(this.requestType != "HUMAN") {

                if ( navigator && navigator.geolocation) {
                    // Activamos el feedback visual
                    this.isLocating = true;

                    const geoOptions = {
                        timeout: 5000, // Máximo 5 segundos de espera
                        enableHighAccuracy: false // 'false' es mucho más rápido
                    };
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            this.userLatitude = position.coords.latitude;
                            this.userLongitude = position.coords.longitude;
                            this.submitLocation = true;
                        },
                        () => {
                            this.userLatitude = "";
                            this.userLongitude = "";
                            console.log("Request Type en location Dialog: " + this.requestType);
                            this.submitLocation = true;
                        },
                        geoOptions // <--- Importante pasar las opciones
                    );
                }
            } else {
                this.userLatitude = "";
                this.userLongitude = "";
                this.submitLocation = true;
                }
        },
	},
};
</script>
